import { useEffect, useRef, useState } from "react";

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";
const CACHE_NAME = "product-photos-v1";

function initData(): string {
  return window.Telegram?.WebApp?.initData ?? "";
}

export type PhotoVariant = "thumb" | "md" | "full";

// Blob URL-ы живут весь сеанс — повторное монтирование той же карточки мгновенное.
const _photoCache = new Map<string, string>();
// Ключи, которые сейчас в процессе загрузки — не дублируем запросы.
const _inFlight = new Map<string, Promise<string>>();

function buildUrl(productId: number, photoIdx: number, variant: PhotoVariant): string {
  const params = new URLSearchParams();
  if (photoIdx > 0) params.set("idx", String(photoIdx));
  if (variant !== "full") params.set("size", variant);
  const qs = params.toString();
  return `${API_BASE}/products/${productId}/photo${qs ? `?${qs}` : ""}`;
}

const keyOf = (productId: number, photoIdx: number, variant: PhotoVariant) =>
  `${productId}|${photoIdx}|${variant}`;

/**
 * Загружает изображение: постоянный Cache Storage (переживает переоткрытие
 * мини-аппа) → сеть. initData передаётся заголовком, поэтому обычный <img src>
 * не подходит — используем fetch+blob.
 */
async function loadBlob(url: string): Promise<Blob> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const hit = await cache.match(url);
    if (hit) return await hit.blob();
    const resp = await fetch(url, { headers: { Authorization: `tma ${initData()}` } });
    if (!resp.ok) throw new Error(String(resp.status));
    cache.put(url, resp.clone()).catch(() => {});
    return await resp.blob();
  } catch {
    const resp = await fetch(url, { headers: { Authorization: `tma ${initData()}` } });
    if (!resp.ok) throw new Error(String(resp.status));
    return await resp.blob();
  }
}

/**
 * Гарантирует, что фото загружено, и возвращает object URL. Результат кэшируется
 * на весь сеанс; параллельные вызовы для одного фото дедуплицируются.
 */
function ensureLoaded(productId: number, photoIdx: number, variant: PhotoVariant): Promise<string> {
  const key = keyOf(productId, photoIdx, variant);
  const cached = _photoCache.get(key);
  if (cached) return Promise.resolve(cached);
  const pending = _inFlight.get(key);
  if (pending) return pending;

  const p = loadBlob(buildUrl(productId, photoIdx, variant))
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      _photoCache.set(key, objectUrl);
      return objectUrl;
    })
    .finally(() => { _inFlight.delete(key); });
  _inFlight.set(key, p);
  return p;
}

/** Фоновая предзагрузка фото (для соседних кадров карусели). Ошибки игнорируются. */
export function prefetchPhoto(productId: number, photoIdx: number, variant: PhotoVariant): void {
  ensureLoaded(productId, photoIdx, variant).catch(() => {});
}

/** Хук: возвращает готовый object URL фото (для произвольной вёрстки, напр. зум). */
export function usePhotoSrc(productId: number, photoIdx: number, variant: PhotoVariant): string | null {
  const [src, setSrc] = useState<string | null>(
    () => _photoCache.get(keyOf(productId, photoIdx, variant)) ?? null,
  );
  useEffect(() => {
    let cancelled = false;
    ensureLoaded(productId, photoIdx, variant)
      .then((u) => { if (!cancelled) setSrc(u); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [productId, photoIdx, variant]);
  return src;
}

interface Props {
  productId: number;
  photoIdx?: number;
  alt?: string;
  rounded?: boolean;
  size?: number;
  /** Размер изображения: thumb (карточки), md (карусель), full (оригинал). */
  variant?: PhotoVariant;
  /** Лёгкий вариант-плейсхолдер, который показывается мгновенно, пока грузится variant. */
  lowVariant?: PhotoVariant;
  /** LQIP data-URI: крошечная размытая заглушка, показывается до загрузки фото. */
  blur?: string | null;
}

export default function ProductPhoto({ productId, photoIdx = 0, alt, rounded, size, variant = "full", lowVariant, blur }: Props) {
  const initial =
    _photoCache.get(keyOf(productId, photoIdx, variant)) ??
    (lowVariant ? _photoCache.get(keyOf(productId, photoIdx, lowVariant)) : undefined) ??
    null;

  const [src, setSrc] = useState<string | null>(initial);
  const [failed, setFailed] = useState(false);
  // Если фото уже в кэше — пропускаем IntersectionObserver и сразу рендерим.
  const [visible, setVisible] = useState(initial !== null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible) return;
    const el = rootRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { rootMargin: "400px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    const targetKey = keyOf(productId, photoIdx, variant);
    const lowKey = lowVariant ? keyOf(productId, photoIdx, lowVariant) : null;

    // Синхронно показываем лучшее, что уже есть в кэше для этого кадра
    // (целевой вариант → плейсхолдер → ничего), чтобы не мелькало чужое фото.
    const cachedTarget = _photoCache.get(targetKey);
    const cachedLow = lowKey ? _photoCache.get(lowKey) : undefined;
    setSrc(cachedTarget ?? cachedLow ?? null);
    setFailed(false);

    if (cachedTarget) return; // целевой вариант готов — грузить нечего

    let cancelled = false;
    let targetLoaded = false;

    // Плейсхолдер: грузим лёгкий вариант, если его ещё нет (показываем до md).
    if (lowKey && !cachedLow) {
      ensureLoaded(productId, photoIdx, lowVariant!)
        .then((url) => { if (!cancelled && !targetLoaded) setSrc(url); })
        .catch(() => {});
    }

    // Целевой вариант: когда готов — заменяем плейсхолдер.
    ensureLoaded(productId, photoIdx, variant)
      .then((url) => { if (!cancelled) { targetLoaded = true; setSrc(url); } })
      .catch(() => { if (!cancelled && !cachedLow && !lowKey) setFailed(true); });

    return () => { cancelled = true; };
  }, [productId, photoIdx, variant, lowVariant, visible]);

  const boxStyle: React.CSSProperties = size ? {
    width: size, height: size, flexShrink: 0,
    background: "var(--secondary-bg)",
    borderRadius: 10,
    overflow: "hidden",
    position: "relative",
  } : {
    aspectRatio: "3/4",
    width: "100%",
    background: "var(--secondary-bg)",
    borderRadius: rounded ? 16 : 0,
    overflow: "hidden",
    position: "relative",
  };

  const imgStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  };

  return (
    <div ref={rootRef} style={boxStyle}>
      {/* Размытая LQIP-заглушка под фото — видна мгновенно, пока грузится картинка. */}
      {blur && !failed && (
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `url(${blur})`,
          backgroundSize: "cover", backgroundPosition: "center",
          filter: "blur(12px)", transform: "scale(1.1)",
        }} />
      )}
      {failed ? (
        <div style={{ ...imgStyle, position: "relative", display: "flex", alignItems: "center",
          justifyContent: "center", color: "#888", fontSize: 12 }}>
          нет фото
        </div>
      ) : src ? (
        <img
          key={src}
          src={src}
          alt={alt ?? ""}
          style={{ ...imgStyle, position: "relative", opacity: 0, transition: "opacity 0.25s ease" }}
          loading="lazy"
          decoding="async"
          onLoad={(e) => { e.currentTarget.style.opacity = "1"; }}
        />
      ) : null}
    </div>
  );
}
